/**
 * LSB steganography decoder — mirrors SteganographyService in ifa-audio-app.
 *
 * Wire format:
 *   [MAGIC  8 B : "IFA_WM01"]
 *   [LENGTH 2 B : uint16 big-endian — payload byte count]
 *   [PAYLOAD N B : UTF-8 message]
 *
 * Each bit lives in the LSB of the R channel of consecutive pixels.
 */

const MAGIC = 'IFA_WM01';
const MAGIC_BYTES = MAGIC.length;
const LENGTH_BYTES = 2;
const HEADER_BYTES = MAGIC_BYTES + LENGTH_BYTES;

function readBytesFromChannel(
  data: Uint8ClampedArray,
  startPixel: number,
  byteCount: number,
): Uint8Array {
  const result = new Uint8Array(byteCount);
  let pixelIdx = startPixel;
  for (let i = 0; i < byteCount; i++) {
    let byte = 0;
    for (let bit = 7; bit >= 0; bit--) {
      byte |= (data[pixelIdx * 4] & 1) << bit;
      pixelIdx++;
    }
    result[i] = byte;
  }
  return result;
}

/**
 * Draw `file` onto an offscreen canvas and extract any embedded watermark.
 * Returns the decoded message string, or null if none found.
 */
export async function extractWatermarkFromFile(file: File): Promise<string | null> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const totalPixels = canvas.width * canvas.height;

  if (totalPixels < HEADER_BYTES * 8) return null;

  const magicBytes = readBytesFromChannel(data, 0, MAGIC_BYTES);
  if (new TextDecoder().decode(magicBytes) !== MAGIC) return null;

  const lenBytes = readBytesFromChannel(data, MAGIC_BYTES * 8, LENGTH_BYTES);
  const payloadLength = (lenBytes[0] << 8) | lenBytes[1];

  if (payloadLength === 0 || (HEADER_BYTES + payloadLength) * 8 > totalPixels) return null;

  const payloadBytes = readBytesFromChannel(data, HEADER_BYTES * 8, payloadLength);
  return new TextDecoder().decode(payloadBytes);
}
